import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, ArrowRight, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, X, Plus } from 'lucide-react';
import AdminCreateAccountDialog from '@/components/admin/AdminCreateAccountDialog';

interface School {
  id: string;
  name: string;
  plan_tier: string | null;
  plan_sub_tier: string | null;
  billing_status: string | null;
  billing_scope: string | null;
  trial_ends_at: string | null;
  created_at: string;
  suspended_at: string | null;
  stripe_customer_id: string | null;
}

type SortField = 'name' | 'plan_tier' | 'billing_status' | 'created_at';
type SortDir = 'asc' | 'desc';

const TIER_COLOURS: Record<string, string> = {
  free: 'secondary',
  individual: 'outline',
  school: 'default',
  trust: 'default',
};

const STATUS_COLOURS: Record<string, string> = {
  active: 'default',
  trialing: 'secondary',
  past_due: 'destructive',
  cancelled: 'destructive',
  paused: 'outline',
};

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (field !== sortField) return <ArrowUpDown className="ml-1 h-3.5 w-3.5 opacity-40 inline" />;
  return sortDir === 'asc'
    ? <ArrowUp className="ml-1 h-3.5 w-3.5 inline" />
    : <ArrowDown className="ml-1 h-3.5 w-3.5 inline" />;
}

export default function AdminSchools() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('__all__');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [suspendedFilter, setSuspendedFilter] = useState('__all__');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [createAccountOpen, setCreateAccountOpen] = useState(false);

  const { data: schools = [], isLoading } = useQuery({
    queryKey: ['admin-schools'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schools')
        .select('id, name, plan_tier, plan_sub_tier, billing_status, billing_scope, trial_ends_at, created_at, suspended_at, stripe_customer_id');
      if (error) throw error;
      return data as School[];
    },
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let result = schools.filter(s => {
      if (q) {
        const nameMatch = s.name.toLowerCase().includes(q);
        const stripeMatch = s.stripe_customer_id?.toLowerCase().includes(q) ?? false;
        if (!nameMatch && !stripeMatch) return false;
      }
      if (planFilter !== '__all__' && s.plan_tier !== planFilter) return false;
      if (statusFilter !== '__all__' && s.billing_status !== statusFilter) return false;
      if (suspendedFilter === 'suspended' && !s.suspended_at) return false;
      if (suspendedFilter === 'active' && s.suspended_at) return false;
      if (dateFrom && s.created_at < dateFrom) return false;
      if (dateTo   && s.created_at.substring(0, 10) > dateTo) return false;
      return true;
    });

    result = [...result].sort((a, b) => {
      let av = a[sortField] ?? '';
      let bv = b[sortField] ?? '';
      if (sortField === 'created_at') {
        av = a.created_at;
        bv = b.created_at;
      }
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [schools, search, planFilter, statusFilter, suspendedFilter, dateFrom, dateTo, sortField, sortDir]);

  const hasActiveFilters = search || planFilter !== '__all__' || statusFilter !== '__all__' || suspendedFilter !== '__all__' || !!dateFrom || !!dateTo;

  const clearFilters = () => {
    setSearch('');
    setPlanFilter('__all__');
    setStatusFilter('__all__');
    setSuspendedFilter('__all__');
    setDateFrom('');
    setDateTo('');
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const truncateStripeId = (id: string | null) => {
    if (!id) return '—';
    // Show prefix + first 8 chars, e.g. "cus_AbCd1234…"
    return id.length > 16 ? `${id.slice(0, 16)}…` : id;
  };

  const thProps = (field: SortField) => ({
    className: 'cursor-pointer select-none whitespace-nowrap',
    onClick: () => toggleSort(field),
  });

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">Schools</h1>
            <p className="text-muted-foreground text-sm mt-1">{schools.length} school{schools.length !== 1 ? 's' : ''} total</p>
          </div>
          <Button onClick={() => setCreateAccountOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />Create account
          </Button>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Name or Stripe customer ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 w-64"
            />
          </div>

          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All plans" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All plans</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="individual">Individual</SelectItem>
              <SelectItem value="school">School</SelectItem>
              <SelectItem value="trust">Trust</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="trialing">Trialing</SelectItem>
              <SelectItem value="past_due">Past due</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
            </SelectContent>
          </Select>

          <Select value={suspendedFilter} onValueChange={setSuspendedFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All accounts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All accounts</SelectItem>
              <SelectItem value="active">Not suspended</SelectItem>
              <SelectItem value="suspended">Suspended only</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground shrink-0">Joined</span>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 w-36 text-sm" />
            <span className="text-xs text-muted-foreground">–</span>
            <Input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   className="h-8 w-36 text-sm" />
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              <X className="h-3.5 w-3.5 mr-1" />Clear
            </Button>
          )}
        </div>

        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">
              {hasActiveFilters
                ? `${filtered.length} of ${schools.length} school${schools.length !== 1 ? 's' : ''}`
                : `${schools.length} school${schools.length !== 1 ? 's' : ''}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 text-sm text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">No schools match the current filters.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead {...thProps('name')}>
                        Name <SortIcon field="name" sortField={sortField} sortDir={sortDir} />
                      </TableHead>
                      <TableHead {...thProps('plan_tier')}>
                        Plan <SortIcon field="plan_tier" sortField={sortField} sortDir={sortDir} />
                      </TableHead>
                      <TableHead>Sub-tier</TableHead>
                      <TableHead {...thProps('billing_status')}>
                        Status <SortIcon field="billing_status" sortField={sortField} sortDir={sortDir} />
                      </TableHead>
                      <TableHead>Stripe ID</TableHead>
                      <TableHead>Trial ends</TableHead>
                      <TableHead {...thProps('created_at')}>
                        Created <SortIcon field="created_at" sortField={sortField} sortDir={sortDir} />
                      </TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(school => (
                      <TableRow
                        key={school.id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/admin/schools/${school.id}`)}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {school.name}
                            {school.suspended_at && (
                              <Badge variant="destructive" className="text-xs gap-1">
                                <AlertTriangle className="h-3 w-3" />Suspended
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={TIER_COLOURS[school.plan_tier ?? ''] as any ?? 'secondary'}>
                            {school.plan_tier ?? '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {school.plan_sub_tier ?? '—'}
                        </TableCell>
                        <TableCell>
                          {school.billing_status ? (
                            <Badge variant={STATUS_COLOURS[school.billing_status] as any ?? 'secondary'}>
                              {school.billing_status.replace('_', ' ')}
                            </Badge>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {school.stripe_customer_id ? (
                            <code className="text-xs font-mono text-muted-foreground">
                              {truncateStripeId(school.stripe_customer_id)}
                            </code>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(school.trial_ends_at)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(school.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={e => { e.stopPropagation(); navigate(`/admin/schools/${school.id}`); }}>
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AdminCreateAccountDialog
        open={createAccountOpen}
        onOpenChange={setCreateAccountOpen}
      />
    </DashboardLayout>
  );
}
